/**
 * SalesByDimensionScreen — net sales sliced by category, customer, hour of day,
 * or weekday. The analytical companion to the Sales report.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadSalesByDimension } from '@/reports/salesByDimension/salesByDimensionDataSource';
import { buildSalesByDimension, type SalesByDimension, type DimRow } from '@/reports/salesByDimension/salesByDimension';

type Dimension = 'category' | 'customer' | 'hour' | 'weekday';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Component ───────────────────────────────────────────────────────────────

export function SalesByDimensionScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [dimension, setDimension] = useState<Dimension>('category');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<SalesByDimension | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const source = await loadSalesByDimension(activeStoreId, from, to);
      setData(buildSalesByDimension(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = dimension === 'category' || dimension === 'customer'
        ? toCsv(dimension === 'category' ? data.byCategory : data.byCustomer, [
            { header: dimension === 'category' ? 'Category' : 'Customer', value: (r) => r.label },
            { header: 'Revenue', value: (r) => r.revenue },
            ...(dimension === 'category' ? [{ header: 'Qty', value: (r: DimRow) => r.quantity }] : []),
            { header: dimension === 'category' ? 'Lines' : 'Bills', value: (r) => r.count },
          ])
        : toCsv(
            (dimension === 'hour' ? data.byHour : data.byWeekday).map((v, i) => ({
              bucket: dimension === 'hour' ? `${i}:00` : WEEKDAYS[i],
              revenue: v,
            })),
            [
              { header: dimension === 'hour' ? 'Hour' : 'Day', value: (r) => r.bucket },
              { header: 'Revenue', value: (r) => r.revenue },
            ],
          );
      await file.saveText({
        content: csv,
        defaultFileName: `SalesBy_${dimension}_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildDimHtml(data, from, to),
        defaultFileName: `SalesByDimension_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  const rows = data ? (dimension === 'category' ? data.byCategory : data.byCustomer) : [];
  const series = data ? (dimension === 'hour' ? data.byHour : data.byWeekday) : [];
  const maxRow = rows.length ? Math.max(...rows.map((r) => Math.abs(r.revenue)), 1) : 1;
  const maxSeries = series.length ? Math.max(...series.map((v) => Math.abs(v)), 1) : 1;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Dimension toggle */}
      <div className="flex flex-wrap items-center gap-2">
        {(['category', 'customer', 'hour', 'weekday'] as Dimension[]).map((d) => (
          <button key={d} type="button" onClick={() => setDimension(d)}
            className={cn('rounded-md px-3 py-1 text-body capitalize transition-colors',
              dimension === d ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {d === 'weekday' ? 'Day of week' : d === 'hour' ? 'Hour of day' : `By ${d}`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.totalRevenue === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No sales in this period.</div>
      ) : dimension === 'category' || dimension === 'customer' ? (
        <Card title={dimension === 'category' ? 'Sales by category' : 'Top customers'}>
          {rows.length === 0 ? (
            <div className="py-4 text-body text-fg-tertiary">No data.</div>
          ) : (
            <table className="w-full text-body">
              <tbody>
                {rows.slice(0, 40).map((r) => (
                  <tr key={r.key} className="border-b border-stroke-hairline/10">
                    <td className="w-1/3 py-1.5 text-fg-primary">{r.label}</td>
                    <td className="py-1.5">
                      <div className="h-2 rounded-full bg-accent/50"
                        style={{ width: `${Math.max(2, Math.round((Math.abs(r.revenue) / maxRow) * 100))}%` }} />
                    </td>
                    <td className="py-1.5 pl-3 text-right tabular-nums text-fg-primary">{formatCurrency(r.revenue)}</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums text-fg-tertiary">
                      {dimension === 'category' ? `${r.count} lines` : `${r.count} bills`}
                    </td>
                    <td className="py-1.5 pl-3 text-right tabular-nums text-fg-tertiary">
                      {pctShare(r.revenue, data.totalRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
        <Card title={dimension === 'hour' ? 'Sales by hour of day' : 'Sales by day of week'}>
          <div className="flex h-48 items-end gap-1">
            {series.map((v, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1"
                title={`${dimension === 'hour' ? `${i}:00` : WEEKDAYS[i]} — ${formatCurrency(v)}`}>
                <div className="flex w-full flex-1 items-end">
                  <div className="w-full rounded-t bg-accent/60"
                    style={{ height: `${Math.round((Math.abs(v) / maxSeries) * 160)}px`, minHeight: v !== 0 ? 2 : 0 }} />
                </div>
                <span className="whitespace-nowrap text-label text-fg-tertiary">
                  {dimension === 'hour' ? (i % 3 === 0 ? i : '') : WEEKDAYS[i]}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function pctShare(value: number, total: number): string {
  if (total <= 0) return '—';
  return `${((value / total) * 100).toFixed(1)}%`;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildDimHtml(d: SalesByDimension, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);
  const share = (v: number) => (d.totalRevenue > 0 ? `${((v / d.totalRevenue) * 100).toFixed(1)}%` : '—');

  const catRows = d.byCategory.map((r) => `<tr><td>${esc(r.label)}</td><td style="text-align:right">${fmt(r.revenue)}</td><td style="text-align:right">${share(r.revenue)}</td></tr>`).join('');
  const custRows = d.byCustomer.slice(0, 25).map((r) => `<tr><td>${esc(r.label)}</td><td style="text-align:right">${fmt(r.revenue)}</td><td style="text-align:right">${r.count}</td></tr>`).join('');
  const weekdayRows = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => `<tr><td>${day}</td><td style="text-align:right">${fmt(d.byWeekday[i])}</td></tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}h2{font-size:13px;margin-top:22px;text-transform:uppercase;color:#666}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}th,td{padding:5px 8px;border-bottom:1px solid #eee}
th{text-align:left;color:#666;font-weight:500}</style></head>
<body>
<h1>Sales by Dimension</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · Net sales ${fmt(d.totalRevenue)}</p>
<h2>By category</h2>
<table><thead><tr><th>Category</th><th style="text-align:right">Revenue</th><th style="text-align:right">Share</th></tr></thead><tbody>${catRows}</tbody></table>
<h2>Top customers</h2>
<table><thead><tr><th>Customer</th><th style="text-align:right">Revenue</th><th style="text-align:right">Bills</th></tr></thead><tbody>${custRows}</tbody></table>
<h2>By day of week</h2>
<table><thead><tr><th>Day</th><th style="text-align:right">Revenue</th></tr></thead><tbody>${weekdayRows}</tbody></table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
